// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import KeybindManager from '@app/features/app/keybindings/KeybindManager';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import InputMonitoringPrompts from '@app/features/voice/state/InputMonitoringPrompts';
import {openNativePermissionSettings, requestNativePermission} from '@app/features/voice/utils/NativePermissions';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useRef, useState} from 'react';

interface InputMonitoringCTAModalProps {
	onComplete?: () => void;
}
export const InputMonitoringCTAModal: React.FC<InputMonitoringCTAModalProps> = observer(({onComplete}) => {
	const {t} = useLingui();
	const [submitting, setSubmitting] = useState(false);
	const initialFocusRef = useRef<HTMLButtonElement | null>(null);
	const handleEnable = async () => {
		setSubmitting(true);
		try {
			const result = await requestNativePermission('input-monitoring');
			if (result === 'granted') {
				ToastCommands.createToast({type: 'success', children: t`Input Monitoring enabled`});
				await KeybindManager.reapplyGlobalShortcuts();
			} else if (result === 'denied') {
				await openNativePermissionSettings('input-monitoring');
				ToastCommands.createToast({
					type: 'info',
					children: t`Please enable ${PRODUCT_NAME} in System Settings → Privacy & Security → Input Monitoring.`,
				});
			}
			InputMonitoringPrompts.dismissInputMonitoringCTA();
			ModalCommands.pop();
			onComplete?.();
		} finally {
			setSubmitting(false);
		}
	};
	const handleDismiss = () => {
		InputMonitoringPrompts.dismissInputMonitoringCTA();
		ModalCommands.pop();
		onComplete?.();
	};
	return (
		<Modal.Root size="small" centered initialFocusRef={initialFocusRef}>
			<Modal.Header title={t`Enable Input Monitoring`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						<Trans>
							Fluxer needs permission to monitor keyboard and mouse input so that <strong>Push-to-Talk</strong> and{' '}
							<strong>Global Shortcuts</strong> work even when you're in another app or game.
						</Trans>
					</Modal.Description>
					<Modal.Description>
						<Trans>
							This is required to detect any key or mouse button you choose for Push-to-Talk. You can change this later
							in <strong>System Settings → Privacy & Security → Input Monitoring</strong>.
						</Trans>
					</Modal.Description>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={handleDismiss} variant="secondary">
					<Trans>Not Now</Trans>
				</Button>
				<Button onClick={handleEnable} submitting={submitting} variant="primary" ref={initialFocusRef}>
					<Trans>Enable Input Monitoring</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
